// ─────────────────────────────────────────
// CreateGroup.js — create a new group
// ─────────────────────────────────────────
// Fields:
//   - Group name
//   - Description
//   - Icon (emoji picker — tap to cycle through options)
//   - Privacy: Public or Private
//   - Member search: type a username to find people from your
//     followers/following list, or use the dropdown to browse all of them

import { useEffect, useState, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import AvatarImg from '../components/AvatarImg'

// Curated list of golf-themed and general emoji for the icon picker
const EMOJI_OPTIONS = [
  '⛳', '🏌️', '🏆', '🎯', '🌿', '🏔️', '🌊', '🌄',
  '🦅', '🐦', '🔥', '⭐', '🎖️', '🤝', '💪', '🏅',
  '🌍', '✈️', '🎉', '📍', '🏡', '🌲', '🍀', '🎱'
]

export default function CreateGroup({ session }) {
  const navigate = useNavigate()

  // ── Form state ───────────────────────────────────────
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [iconEmoji, setIconEmoji] = useState('⛳')
  const [privacy, setPrivacy] = useState('public')
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)

  // ── Member search state ───────────────────────────────
  // connections = everyone the user follows + everyone following them
  const [connections, setConnections] = useState([])   // full list for dropdown
  const [memberSearch, setMemberSearch] = useState('') // what's typed in the search box
  const [searchResults, setSearchResults] = useState([]) // filtered results
  const [invitedMembers, setInvitedMembers] = useState([]) // people added to the group
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef(null)

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // ── Load connections on mount ─────────────────────────
  useEffect(() => {
    fetchConnections()
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Filter connections as user types
  useEffect(() => {
    if (!memberSearch.trim()) {
      setSearchResults(connections)
      return
    }
    const q = memberSearch.toLowerCase()
    setSearchResults(
      connections.filter(c => c.username.toLowerCase().includes(q))
    )
  }, [memberSearch, connections])

  async function fetchConnections() {
    // People the current user follows
    const { data: following } = await supabase
      .from('follows')
      .select('profiles!follows_following_id_fkey(id, username, avatar_emoji, avatar_url)')
      .eq('follower_id', session.user.id)

    // People following the current user
    const { data: followers } = await supabase
      .from('follows')
      .select('profiles!follows_follower_id_fkey(id, username, avatar_emoji, avatar_url)')
      .eq('following_id', session.user.id)

    // Merge and deduplicate by user id
    const seen = new Set()
    const all = []
    ;[...(following || []), ...(followers || [])].forEach(row => {
      const profile = row.profiles
      if (profile && !seen.has(profile.id)) {
        seen.add(profile.id)
        all.push(profile)
      }
    })

    // Sort alphabetically
    all.sort((a, b) => a.username.localeCompare(b.username))
    setConnections(all)
    setSearchResults(all)
  }

  // ── Member management ────────────────────────────────
  function addMember(profile) {
    if (invitedMembers.find(m => m.id === profile.id)) return // already added
    setInvitedMembers(prev => [...prev, profile])
    setMemberSearch('')
    setShowDropdown(false)
  }

  function removeMember(id) {
    setInvitedMembers(prev => prev.filter(m => m.id !== id))
  }

  // ── Submit ───────────────────────────────────────────
  async function handleCreate() {
    if (!name.trim()) { setError('Group name is required.'); return }
    setSaving(true)
    setError(null)

    // Step 1: Create the group row
    const { data: group, error: groupError } = await supabase
      .from('groups')
      .insert({
        name: name.trim(),
        description: description.trim() || null,
        icon_emoji: iconEmoji,
        privacy,
        created_by: session.user.id,
        member_count: 1 + invitedMembers.length
      })
      .select()
      .single()

    if (groupError) {
      setError(groupError.message)
      setSaving(false)
      return
    }

    // Step 2: Add the creator as admin first (separate insert so RLS passes)
    const { error: adminError } = await supabase
      .from('group_members')
      .insert({ group_id: group.id, user_id: session.user.id, role: 'admin' })

    if (adminError) {
      setError(adminError.message)
      setSaving(false)
      return
    }

    // Step 3: Add invited members (now the creator is in the table so admin RLS check passes)
    if (invitedMembers.length > 0) {
      await supabase.from('group_members').insert(
        invitedMembers.map(m => ({ group_id: group.id, user_id: m.id, role: 'member' }))
      )
    }

    // Navigate to the new group page
    navigate(`/groups/${group.id}`)
  }

  // ── RENDER ───────────────────────────────────────────

  return (
    <div style={styles.page}>

      {/* ── TOP BAR ── */}
      <div style={styles.topbar}>
        <Link to="/groups" style={styles.back}>← Groups</Link>
        <div style={styles.topbarTitle}>Create a Group</div>
        <div style={{ width: 70 }} />
      </div>

      <div style={styles.body}>

        {/* ── ICON PICKER ── */}
        <div style={styles.iconSection}>
          <div
            style={styles.iconPreview}
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          >
            {iconEmoji}
          </div>
          <div style={styles.iconHint}>Tap to change icon</div>

          {/* Emoji grid */}
          {showEmojiPicker && (
            <div style={styles.emojiGrid}>
              {EMOJI_OPTIONS.map(emoji => (
                <button
                  key={emoji}
                  style={{
                    ...styles.emojiOption,
                    background: emoji === iconEmoji ? '#e8f5ed' : 'transparent',
                    border: emoji === iconEmoji ? '2px solid #2aad56' : '2px solid transparent'
                  }}
                  onClick={() => { setIconEmoji(emoji); setShowEmojiPicker(false) }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── GROUP NAME ── */}
        <div style={styles.field}>
          <label style={styles.label}>Group Name *</label>
          <input
            style={styles.input}
            placeholder="e.g. Saturday Morning Crew"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={60}
          />
        </div>

        {/* ── DESCRIPTION ── */}
        <div style={styles.field}>
          <label style={styles.label}>Description</label>
          <textarea
            style={{ ...styles.input, minHeight: 80, resize: 'none' }}
            placeholder="What's this group about?"
            value={description}
            onChange={e => setDescription(e.target.value)}
            maxLength={280}
          />
        </div>

        {/* ── PRIVACY ── */}
        <div style={styles.field}>
          <label style={styles.label}>Privacy</label>
          <div style={styles.privacyRow}>
            <button
              style={{
                ...styles.privacyBtn,
                ...(privacy === 'public' ? styles.privacyActive : {})
              }}
              onClick={() => setPrivacy('public')}
            >
              <div style={styles.privacyIcon}>🌐</div>
              <div>
                <div style={styles.privacyBtnLabel}>Public</div>
                <div style={styles.privacyBtnDesc}>Anyone can find and join</div>
              </div>
            </button>
            <button
              style={{
                ...styles.privacyBtn,
                ...(privacy === 'private' ? styles.privacyActive : {})
              }}
              onClick={() => setPrivacy('private')}
            >
              <div style={styles.privacyIcon}>🔒</div>
              <div>
                <div style={styles.privacyBtnLabel}>Private</div>
                <div style={styles.privacyBtnDesc}>Invite only</div>
              </div>
            </button>
          </div>
        </div>

        {/* ── ADD MEMBERS ── */}
        <div style={styles.field}>
          <label style={styles.label}>Add Members</label>

          {/* Invited members chips */}
          {invitedMembers.length > 0 && (
            <div style={styles.invitedRow}>
              {invitedMembers.map(m => (
                <div key={m.id} style={styles.invitedChip}>
                  <span style={styles.invitedAvatar}><AvatarImg profile={m} /></span>
                  <span style={styles.invitedName}>@{m.username}</span>
                  <button style={styles.removeChip} onClick={() => removeMember(m.id)}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Search input + dropdown */}
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <div style={styles.memberSearchBox}>
              <span style={{ fontSize: 15 }}>🔍</span>
              <input
                style={styles.memberSearchInput}
                placeholder={connections.length > 0 ? `Search your ${connections.length} connections...` : 'No connections yet'}
                value={memberSearch}
                onChange={e => { setMemberSearch(e.target.value); setShowDropdown(true) }}
                onFocus={() => setShowDropdown(true)}
                disabled={connections.length === 0}
              />
              {/* Chevron to open/close the full dropdown list */}
              <button
                style={styles.dropdownToggle}
                onClick={() => setShowDropdown(!showDropdown)}
                disabled={connections.length === 0}
              >
                {showDropdown ? '▲' : '▼'}
              </button>
            </div>

            {/* Dropdown list */}
            {showDropdown && connections.length > 0 && (
              <div style={styles.dropdown}>
                {searchResults.length === 0 && (
                  <div style={styles.dropdownEmpty}>No matches found</div>
                )}
                {searchResults.map(profile => {
                  const already = invitedMembers.find(m => m.id === profile.id)
                  return (
                    <div
                      key={profile.id}
                      style={{
                        ...styles.dropdownRow,
                        opacity: already ? 0.4 : 1,
                        cursor: already ? 'default' : 'pointer'
                      }}
                      onMouseDown={() => !already && addMember(profile)}
                    >
                      <div style={styles.dropdownAvatar}><AvatarImg profile={profile} /></div>
                      <div style={styles.dropdownUsername}>@{profile.username}</div>
                      {already
                        ? <div style={styles.dropdownAdded}>Added ✓</div>
                        : <div style={styles.dropdownAdd}>＋ Add</div>
                      }
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {connections.length === 0 && !saving && (
            <div style={styles.noConnectionsHint}>
              Follow other users first to invite them to groups.
            </div>
          )}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* ── SUMMARY ── */}
        <div style={styles.summary}>
          <div style={styles.summaryIcon}>{iconEmoji}</div>
          <div style={styles.summaryInfo}>
            <div style={styles.summaryName}>{name || 'Your group name'}</div>
            <div style={styles.summaryMeta}>
              {privacy === 'public' ? '🌐 Public' : '🔒 Private'} ·{' '}
              {1 + invitedMembers.length} member{1 + invitedMembers.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <button
          style={{ ...styles.createBtn, opacity: saving || !name.trim() ? 0.5 : 1 }}
          onClick={handleCreate}
          disabled={saving || !name.trim()}
        >
          {saving ? 'Creating...' : '🏌️ Create Group'}
        </button>

      </div>
    </div>
  )
}

// ── STYLES ───────────────────────────────────────────────────

const styles = {
  page: { minHeight: '100vh', background: '#f4f7f5', fontFamily: "'Barlow', sans-serif" },

  topbar: {
    background: '#0e1f13', padding: '14px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    position: 'sticky', top: 0, zIndex: 10
  },
  back: { color: '#c8f135', textDecoration: 'none', fontSize: 14, fontWeight: 600, minWidth: 70 },
  topbarTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 18, color: 'white', letterSpacing: 0.5
  },

  body: { maxWidth: 500, margin: '0 auto', padding: '20px 16px 60px' },

  // ── Icon picker ───────────────────────────────────────
  iconSection: { textAlign: 'center', marginBottom: 24 },
  iconPreview: {
    width: 80, height: 80, borderRadius: 22,
    background: 'linear-gradient(135deg, #1a7a3c, #0e1f13)',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 40, cursor: 'pointer', marginBottom: 8,
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
  },
  iconHint: { fontSize: 12, color: '#6b7c6e' },
  emojiGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)',
    gap: 6, marginTop: 12, background: 'white',
    borderRadius: 16, padding: 12,
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
  },
  emojiOption: {
    width: '100%', aspectRatio: '1', fontSize: 22,
    borderRadius: 10, cursor: 'pointer', background: 'transparent'
  },

  // ── Form fields ───────────────────────────────────────
  field: { marginBottom: 20 },
  label: {
    display: 'block', fontSize: 11, fontWeight: 700,
    letterSpacing: 1.5, textTransform: 'uppercase',
    color: '#6b7c6e', marginBottom: 8
  },
  input: {
    width: '100%', background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 12, padding: '13px 14px', fontSize: 15,
    color: '#0e1f13', outline: 'none', fontFamily: "'Barlow', sans-serif",
    boxSizing: 'border-box'
  },

  // ── Privacy toggle ────────────────────────────────────
  privacyRow: { display: 'flex', gap: 10 },
  privacyBtn: {
    flex: 1, background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 14, padding: '14px 12px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
    fontFamily: "'Barlow', sans-serif"
  },
  privacyActive: { background: '#e8f5ed', borderColor: '#2aad56' },
  privacyIcon: { fontSize: 22, flexShrink: 0 },
  privacyBtnLabel: { fontWeight: 700, fontSize: 14, color: '#0e1f13', marginBottom: 2 },
  privacyBtnDesc: { fontSize: 11, color: '#6b7c6e' },

  // ── Member search ─────────────────────────────────────
  invitedRow: {
    display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10
  },
  invitedChip: {
    display: 'flex', alignItems: 'center', gap: 4,
    background: '#e8f5ed', borderRadius: 20, padding: '5px 10px 5px 6px',
    fontSize: 12, fontWeight: 600, color: '#1a7a3c',
    border: '1px solid rgba(42,173,86,0.3)'
  },
  invitedAvatar: { fontSize: 14 },
  invitedName: { fontSize: 12 },
  removeChip: {
    background: 'none', border: 'none', color: '#6b7c6e',
    fontSize: 11, cursor: 'pointer', padding: '0 0 0 2px', lineHeight: 1
  },
  memberSearchBox: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 12, padding: '11px 12px'
  },
  memberSearchInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: 14,
    color: '#0e1f13', background: 'transparent', fontFamily: "'Barlow', sans-serif"
  },
  dropdownToggle: {
    background: 'none', border: 'none', fontSize: 10,
    color: '#6b7c6e', cursor: 'pointer', padding: '0 2px'
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
    background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 12, marginTop: 4, maxHeight: 220, overflowY: 'auto',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
  },
  dropdownRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 14px', borderBottom: '1px solid #f4f7f5'
  },
  dropdownAvatar: {
    width: 32, height: 32, borderRadius: '50%', fontSize: 16,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  dropdownUsername: { flex: 1, fontWeight: 600, fontSize: 13, color: '#0e1f13' },
  dropdownAdd: { fontSize: 12, fontWeight: 700, color: '#1a7a3c' },
  dropdownAdded: { fontSize: 11, color: '#6b7c6e' },
  dropdownEmpty: { padding: '14px', textAlign: 'center', fontSize: 13, color: '#6b7c6e' },
  noConnectionsHint: {
    marginTop: 8, fontSize: 12, color: '#6b7c6e', fontStyle: 'italic'
  },

  error: {
    background: 'rgba(232,64,64,0.1)', border: '1px solid rgba(232,64,64,0.3)',
    color: '#c0392b', borderRadius: 10, padding: '10px 14px',
    fontSize: 13, marginBottom: 16
  },

  // ── Preview + submit ──────────────────────────────────
  summary: {
    background: '#0e1f13', borderRadius: 16, padding: '16px',
    display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16
  },
  summaryIcon: {
    width: 52, height: 52, borderRadius: 14,
    background: 'rgba(255,255,255,0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 28, flexShrink: 0
  },
  summaryInfo: {},
  summaryName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 18, color: 'white', letterSpacing: 0.5
  },
  summaryMeta: { fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 3 },

  createBtn: {
    width: '100%', background: '#c8f135', color: '#0e1f13',
    border: 'none', borderRadius: 14, padding: '18px',
    fontSize: 18, fontWeight: 900, cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1
  }
}
